/**
 * AI 供应商的数据请求（TanStack Query），接口与参数与旧版 AIProviderView.vue 一致
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { aiProvidersApi } from '@/api/services/aiProviders'
import type { AIProvider, CostCurrency, ProviderFormValues } from './types'

const PROVIDERS_KEY = ['admin', 'ai-providers'] as const

// 列表可能是数组，也可能是 { data: [...] } 包装格式
export function normalizeProviderList(payload: unknown): AIProvider[] {
  if (Array.isArray(payload)) return payload
  const wrapped = (payload as { data?: unknown } | null)?.data
  return Array.isArray(wrapped) ? wrapped : []
}

export function useProviderList() {
  return useQuery({
    queryKey: [...PROVIDERS_KEY, 'list'],
    queryFn: async () => normalizeProviderList((await aiProvidersApi.getList()).data)
  })
}

// 组装提交数据：编辑时密钥留空表示不修改；去掉未填的字段
export function buildProviderPayload(values: ProviderFormValues, isEdit: boolean): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    ...values,
    cost_currency: values.cost_currency === 'CNY' ? 'CNY' : 'USD'
  }
  if (isEdit && !values.api_key) delete payload.api_key
  for (const key of Object.keys(payload)) {
    if (payload[key] === undefined || payload[key] === null) delete payload[key]
  }
  return payload
}

export function useSaveProvider() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, values }: { id?: number; values: ProviderFormValues }) => {
      const payload = buildProviderPayload(values, id !== undefined)
      return id === undefined ? aiProvidersApi.create(payload) : aiProvidersApi.update(id, payload)
    },
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY })
  })
}

export function useDeleteProvider() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => aiProvidersApi.delete(id),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY })
  })
}

export function useToggleDefaultProvider() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => aiProvidersApi.toggleDefault(id),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PROVIDERS_KEY })
  })
}

export interface FetchedPrice {
  found: boolean
  input?: number
  output?: number
  currency: CostCurrency
  fromLiteLLM: boolean
  message?: string
}

// 价格接口可能返回 { data: {...} } 包装，也可能直接返回；字段名有两种写法
export function parsePriceResponse(responseData: any): FetchedPrice {
  const data = responseData ?? {}
  const price = data.data ?? data
  const currency = String(price.currency ?? price.cost_currency ?? 'USD').toUpperCase()
  return {
    found: Boolean(price.success ?? data.success ?? true),
    input: price.input_cost ?? price.input_cost_per_million,
    output: price.output_cost ?? price.output_cost_per_million,
    currency: currency === 'CNY' ? 'CNY' : 'USD',
    fromLiteLLM: price.source === 'litellm',
    message: price.message || data.message
  }
}

export function useFetchModelPrice() {
  return useMutation({
    mutationFn: async ({ modelName, providerName }: { modelName: string; providerName: string }) =>
      parsePriceResponse((await aiProvidersApi.fetchPrice(modelName, providerName)).data),
    retry: false
  })
}
